import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../api";

/*
 * THE LIVE PREVIEW beside the Page tab's editors: the real lead pages, in a
 * phone-sized frame, with his current settings. Asked for 2026-09-30.
 *
 * Not a mock of the pages: the frame loads /book itself, with a fifteen-minute
 * pass from the server that opens the pages while they are switched off and
 * unlocks nothing that writes. So what he sees here is what a lead gets, and a
 * change to the lead pages can never leave the preview behind.
 *
 * `version` bumps after every save, which reloads the frame.
 */

const VIEWS = [
  { key: "page", label: "Booking page" },
  { key: "personal", label: "Personal link" },
  { key: "success", label: "Success screen" },
];

// The pass lasts fifteen minutes; ask for a new one a little before that.
const REFRESH_MS = 13 * 60 * 1000;

function srcFor(view, token) {
  const p = encodeURIComponent(token);
  if (view === "personal") return `/book?p=${p}&as=personal`;
  if (view === "success") return `/book/booked/preview?p=${p}&as=personal`;
  // A meeting type's own page: "type:<slug>".
  if (view.startsWith("type:")) return `/book/${encodeURIComponent(view.slice(5))}?p=${p}`;
  return `/book?p=${p}`;
}

export default function PagePreview({ version, types = [] }) {
  const [view, setView] = useState("page");
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);

  const mint = useCallback(() => {
    api("/booking/preview-token", { method: "POST" })
      .then((r) => {
        setToken(r.token);
        setError(null);
      })
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    mint();
    const id = setInterval(mint, REFRESH_MS);
    return () => clearInterval(id);
  }, [mint]);

  const src = token ? srcFor(view, token) : null;

  return (
    <aside className="crm-bkpreview">
      <div className="crm-bkpreview__head">
        <span className="crm-bkmono">Preview</span>
        {src ? (
          <a className="crm-linkact" href={src} target="_blank" rel="noreferrer">
            Open ↗
          </a>
        ) : null}
      </div>
      <span className="crm-bkseg crm-bkpreview__views">
        {[...VIEWS, ...types.filter((t) => t.enabled !== false && t.slug).map((t) => ({ key: `type:${t.slug}`, label: t.name }))].map((v) => (
          <button key={v.key} type="button" className={view === v.key ? "sel" : ""} onClick={() => setView(v.key)}>
            {v.label}
          </button>
        ))}
      </span>
      <div className="crm-bkpreview__phone">
        {error ? (
          <p className="crm-hint crm-hint--warn">{error}</p>
        ) : src ? (
          <iframe key={`${view}:${version}:${token}`} title="Lead page preview" src={src} />
        ) : (
          <p className="crm-hint">Loading…</p>
        )}
      </div>
      <p className="crm-hint">
        {view === "personal"
          ? "As Jane Doe at Acme, arriving from her personal link."
          : view === "success"
          ? "Right after booking. Tap an agenda to see its lines; nothing is saved."
          : "What anyone with the link sees. Tap a time and Book to reach the success screen."}
      </p>
    </aside>
  );
}
